import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { Modal } from '../../components/Common';
import { PageHeader } from '../../components/Layout';
import {
  consultationService,
  BlockedDateEntry,
} from '../../services';
import { handleApiError, handleApiSuccess } from '../../services/error.service';

const SECTIONS = [
  { value: 'remoteCall', label: 'Remote Call' },
  { value: 'assistantVisit', label: 'Assistant Visit' },
];

const formatDate = (date: Dayjs | null): string =>
  date ? date.format('YYYY-MM-DD') : '';

const formatDisplayDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const BlockedDatesSection: React.FC<{
  type: string;
  title: string;
  data: BlockedDateEntry[];
  loading: boolean;
  onDelete: (entry: BlockedDateEntry) => void;
}> = ({ type, title, data, loading, onDelete }) => (
  <Paper sx={{ overflow: 'auto' }}>
    <TableContainer>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell colSpan={5}>
              <Typography variant="subtitle1" fontWeight={600}>
                {title}
              </Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Start Date</TableCell>
            <TableCell>End Date</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No blocked dates
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            data.map(entry => (
              <TableRow key={entry.availabilityId} hover>
                <TableCell>{formatDisplayDate(entry.startDate)}</TableCell>
                <TableCell>{formatDisplayDate(entry.endDate)}</TableCell>
                <TableCell>{entry.startTime} - {entry.endTime}</TableCell>
                <TableCell>{formatDisplayDate(entry.createdAt)}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(entry)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

const Consultation: React.FC = () => {
  const [remoteDates, setRemoteDates] = useState<BlockedDateEntry[]>([]);
  const [visitDates, setVisitDates] = useState<BlockedDateEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [consultationType, setConsultationType] = useState(SECTIONS[0].value);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [submitting, setSubmitting] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [remote, visit] = await Promise.all([
        consultationService.getBlockedDates('remoteCall'),
        consultationService.getBlockedDates('assistantVisit'),
      ]);
      setRemoteDates(remote);
      setVisitDates(visit);
    } catch (err) {
      handleApiError(err);
      setError('Failed to load blocked dates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleAdd = async () => {
    if (!consultationType || !startDate || !endDate) return;

    try {
      setSubmitting(true);
      await consultationService.addBlockedDate({
        consultationType,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        startTime,
        endTime,
      });
      handleApiSuccess('Blocked date added successfully');
      setAddModalOpen(false);
      setConsultationType(SECTIONS[0].value);
      setStartDate(null);
      setEndDate(null);
      setStartTime('00:00');
      setEndTime('23:59');
      fetchAll();
    } catch (err) {
      handleApiError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entry: BlockedDateEntry) => {
    try {
      await consultationService.removeBlockedDate(
        entry.availabilityId,
        entry.consultationType,
      );
      handleApiSuccess('Blocked date removed successfully');
      fetchAll();
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleCloseModal = () => {
    setAddModalOpen(false);
    setConsultationType(SECTIONS[0].value);
    setStartDate(null);
    setEndDate(null);
    setStartTime('00:00');
    setEndTime('23:59');
  };

  return (
    <Box
      className="page-container"
      sx={{
        padding: { xs: '24px 16px', sm: '40px 40px', md: '40px 80px' },
        gap: '24px',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <PageHeader
        title="Event Planner Availability"
        actionButtonText="Block Date"
        onActionClick={() => setAddModalOpen(true)}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflow: 'auto' }}>
        <BlockedDatesSection
          type="remoteCall"
          title="Remote Call"
          data={remoteDates}
          loading={loading}
          onDelete={handleDelete}
        />
        <BlockedDatesSection
          type="assistantVisit"
          title="Assistant Visit"
          data={visitDates}
          loading={loading}
          onDelete={handleDelete}
        />
      </Box>

      <Modal
        open={addModalOpen}
        onClose={handleCloseModal}
        title="Block Date Range"
        width={480}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Consultation Type</InputLabel>
            <Select
              value={consultationType}
              label="Consultation Type"
              onChange={e => setConsultationType(e.target.value)}
            >
              {SECTIONS.map(t => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            disablePast
            format="YYYY-MM-DD"
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            disablePast
            format="YYYY-MM-DD"
            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
            minDate={startDate || dayjs()}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Start Time"
              type="time"
              value={startTime}
              onChange={e => setStartTime(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
            <TextField
              label="End Time"
              type="time"
              value={endTime}
              onChange={e => setEndTime(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 300 }}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={!consultationType || !startDate || !endDate || submitting}
              startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
            >
              {submitting ? 'Adding...' : 'Block Dates'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default Consultation;
